import React, { useState, useEffect } from 'react';
import { Col, Row, Container, Alert } from 'react-bootstrap';
import { authService } from '../../../../services/api';
import { useAuth } from '../../../../contexts/AuthContext';
import Loading from '../../../common/Loading';
import ProfileBanner from './ProfileBanner';
import ProfileInfo from './ProfileInfo';
import ProfileStats from './ProfileStats';
import ProfileSettings from './ProfileSettings';
import ProfileActivityLog from './ProfileActivityLog';
import { toast } from 'react-toastify';

const Profile = () => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);
  const { user, updateUser } = useAuth();

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authService.getProfile();
      // Manejar la estructura real del API que incluye data
      const userData = response.data || response;
      setProfileData(userData);
    } catch (error) {
      console.error('Error loading profile:', error);
      setError('Error al cargar el perfil del usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (updateData) => {
    try {
      setUpdating(true);
      const response = await authService.updateProfile(updateData);
      const updatedUser = response.data || response;
      setProfileData(updatedUser);
      toast.success('Perfil actualizado exitosamente');
      return true;
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Error al actualizar el perfil');
      return false;
    } finally {
      setUpdating(false);
    }
  };

  const handleImageUpdate = (newData) => {
    // Si recibimos un usuario completo, usarlo; si no, solo actualizar la URL
    if (typeof newData === 'object' && newData.id) {
      // Es un usuario completo
      setProfileData(newData);
      // IMPORTANTE: También actualizar el contexto global para el navbar
      updateUser(newData);
    } else {
      // Es solo una URL de imagen
      const updatedProfile = {
        ...profileData,
        profileImageUrl: newData,
        updatedAt: new Date().toISOString() // Actualizar timestamp para forzar re-render
      };
      setProfileData(updatedProfile);
      // IMPORTANTE: También actualizar el contexto global para el navbar
      updateUser(updatedProfile);
    }
  };

  useEffect(() => {
    if (user) {
      loadProfile();
    }
  }, [user]);

  if (loading) {
    return (
      <Container>
        <Loading message="Cargando perfil..." />
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert variant="danger" className="mt-3">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </Container>
    );
  }

  if (!profileData) {
    return (
      <Container>
        <Alert variant="warning" className="mt-3">
          <Alert.Heading>Sin datos</Alert.Heading>
          <p>No se pudo cargar la información del perfil</p>
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <ProfileBanner 
        user={profileData} 
        onUpdate={handleUpdateProfile}
        updating={updating}
        onImageUpdate={handleImageUpdate}
      />
      
      {/* Primera fila: Información Personal y Actividad Reciente */}
      <Row className="g-3 mb-3">
        <Col xl={8} lg={8} md={12}>
          <ProfileInfo 
            user={profileData} 
            onUpdate={handleUpdateProfile}
            updating={updating}
          />
        </Col>
        <Col xl={4} lg={4} md={12}>
          <ProfileActivityLog user={profileData} />
        </Col>
      </Row>

      {/* Segunda fila: Configuraciones y Estadísticas */}
      <Row className="g-3 mb-3">
        <Col xl={8} lg={8} md={12}>
          <ProfileSettings 
            user={profileData} 
            onUpdate={handleUpdateProfile}
            updating={updating}
          />
        </Col>
        <Col xl={4} lg={4} md={12}>
          <ProfileStats user={profileData} />
        </Col>
      </Row>
    </>
  );
};

export default Profile;
